import React from "react";

/**
 * PolishedReportView — render a knowledge-grounded report draft as a
 * structured document instead of a flat <pre> block.
 *
 * Lightweight Markdown-ish renderer (no extra dep):
 *   - Lines starting with `## `  → h3 heading
 *   - Lines starting with `### ` → h4 heading
 *   - Lines starting with `- ` or `* ` → bullet items (grouped into <ul>)
 *   - Blank line → paragraph break
 *   - Lines wrapped in **bold** → <strong>
 *   - Everything else → paragraph text
 *
 * The renderer is deliberately conservative — it never executes user
 * content as HTML, only as text nodes. All output is React elements.
 */

const BOLD_RE = /\*\*([^*]+)\*\*/g;

function renderInline(text, keyPrefix) {
  // Split by **bold** markers while preserving order.
  const parts = [];
  let lastIndex = 0;
  let i = 0;
  let match;
  // Reset regex state for each call.
  BOLD_RE.lastIndex = 0;
  while ((match = BOLD_RE.exec(text)) !== null) {
    if (match.index > lastIndex) {
      parts.push(text.slice(lastIndex, match.index));
    }
    parts.push(
      <strong key={`${keyPrefix}-b-${i++}`} className="font-semibold text-[var(--rs-text-1)]">
        {match[1]}
      </strong>
    );
    lastIndex = match.index + match[0].length;
  }
  if (lastIndex < text.length) parts.push(text.slice(lastIndex));
  return parts.length > 0 ? parts : [text];
}

export default function PolishedReportView({ text, testid }) {
  const safe = String(text || "");
  if (!safe.trim()) return null;

  const lines = safe.replace(/\r\n/g, "\n").split("\n");

  const blocks = [];
  let paragraphBuffer = [];
  let listBuffer = [];
  // Markdown tables: the system prompt MANDATES table format for Section 1
  // (Operations Summary) and Section 2 (Well Control & Mud), so without this
  // those sections rendered as raw pipe characters in a paragraph.
  let tableBuffer = [];      // array of row-cell arrays
  let tableHasHeader = false; // set when a |---|---| separator row is consumed

  const flushParagraph = () => {
    if (paragraphBuffer.length === 0) return;
    const key = `p-${blocks.length}`;
    blocks.push(
      <p
        key={key}
        className="text-[12.5px] leading-relaxed text-[var(--rs-text-2)] mb-2"
      >
        {paragraphBuffer.map((seg, i) => (
          <React.Fragment key={`${key}-seg-${i}`}>
            {i > 0 && " "}
            {renderInline(seg, `${key}-${i}`)}
          </React.Fragment>
        ))}
      </p>
    );
    paragraphBuffer = [];
  };

  const flushList = () => {
    if (listBuffer.length === 0) return;
    const key = `ul-${blocks.length}`;
    blocks.push(
      <ul
        key={key}
        className="list-disc pl-5 mb-3 space-y-1 text-[12.5px] leading-relaxed text-[var(--rs-text-2)] marker:text-[var(--rs-amber)]"
      >
        {listBuffer.map((item, i) => (
          <li key={`${key}-li-${i}`}>{renderInline(item, `${key}-${i}`)}</li>
        ))}
      </ul>
    );
    listBuffer = [];
  };

  const flushTable = () => {
    if (tableBuffer.length === 0) { tableHasHeader = false; return; }
    const key = `tbl-${blocks.length}`;
    const rows = tableBuffer;
    const headRow = tableHasHeader ? rows[0] : null;
    const bodyRows = tableHasHeader ? rows.slice(1) : rows;
    blocks.push(
      <div key={key} className="overflow-x-auto mb-3">
        <table className="w-full border-collapse text-[12px] leading-relaxed text-[var(--rs-text-2)]">
          {headRow && (
            <thead>
              <tr>
                {headRow.map((cell, i) => (
                  <th key={`${key}-th-${i}`}
                      className="text-left font-semibold px-2 py-1 border-b"
                      style={{ borderColor: "var(--rs-border)", color: "var(--rs-text-1)" }}>
                    {renderInline(cell, `${key}-th-${i}`)}
                  </th>
                ))}
              </tr>
            </thead>
          )}
          <tbody>
            {bodyRows.map((cells, r) => (
              <tr key={`${key}-tr-${r}`}>
                {cells.map((cell, c) => (
                  <td key={`${key}-td-${r}-${c}`}
                      className="align-top px-2 py-1 border-b"
                      style={{ borderColor: "var(--rs-border)" }}>
                    {renderInline(cell, `${key}-td-${r}-${c}`)}
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    );
    tableBuffer = [];
    tableHasHeader = false;
  };

  for (let raw of lines) {
    const line = raw.replace(/\s+$/, "");
    if (!line.trim()) {
      flushParagraph();
      flushList();
      flushTable();
      continue;
    }
    // A table row must START with a pipe, so ordinary prose containing a
    // pipe is never mistaken for one.
    if (/^\s*\|/.test(line)) {
      flushParagraph();
      flushList();
      const cells = line.trim().replace(/^\|/, "").replace(/\|$/, "").split("|").map((c) => c.trim());
      // Separator row (|---|:--:|) is consumed, not rendered; it marks the
      // row above it as the header.
      if (cells.every((c) => /^:?-{2,}:?$/.test(c))) {
        if (tableBuffer.length > 0) tableHasHeader = true;
        continue;
      }
      tableBuffer.push(cells);
      continue;
    }
    flushTable();
    const h3 = line.match(/^##\s+(.*)$/);
    if (h3) {
      flushParagraph();
      flushList();
      blocks.push(
        <h3
          key={`h3-${blocks.length}`}
          className="text-[13.5px] font-semibold tracking-wide text-[var(--rs-amber)] mt-3 mb-1.5 uppercase"
        >
          {h3[1].trim()}
        </h3>
      );
      continue;
    }
    const h4 = line.match(/^###\s+(.*)$/);
    if (h4) {
      flushParagraph();
      flushList();
      blocks.push(
        <h4
          key={`h4-${blocks.length}`}
          className="text-[12.5px] font-semibold text-[var(--rs-text-1)] mt-2 mb-1"
        >
          {h4[1].trim()}
        </h4>
      );
      continue;
    }
    const bullet = line.match(/^\s*[-*]\s+(.*)$/);
    if (bullet) {
      flushParagraph();
      listBuffer.push(bullet[1]);
      continue;
    }
    flushList();
    paragraphBuffer.push(line.trim());
  }
  flushParagraph();
  flushList();
  flushTable();

  return (
    <div
      data-testid={testid}
      className="rs-polished-report rounded-md"
      style={{
        background: "rgba(10,11,14,0.6)",
        padding: 16,
        maxHeight: 520,
        overflowY: "auto",
        fontFamily: "Inter, ui-sans-serif, system-ui, -apple-system, sans-serif",
      }}
    >
      {blocks}
    </div>
  );
}
